import { useContext, useState } from 'react';

import ArtlistDataContext from '@/context/ArtlistDataContext';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import autoCropImage from '@/pages/artwork/artwork-header-mobile/options/download-template/components/AutoCrop';
import UserService from '@/services/user.service';
import ArtworkImage from '@/shared-components/cards/components/ArtworkImage';
import AddIcon from '@/shared-components/icons/components/actions/AddIcon';
import PublicSuccessCheck from '@/shared-components/icons/components/public/PublicSuccessCheck';
import AddArtworkToArtlistLoader from '@/shared-components/loader/special-loaders/AddArtworkToArtlistLoader';
import Text from '@/shared-components/text/components/Text';
import { getArtworkArtistDisplayName } from '@/utils/artworkArtists';

import './styles/Row.css';

export default function Row({ artwork, isInArtlist }) {
  const { artlistData, setArtlistData } = useContext(ArtlistDataContext);
  const t = useTranslations('common.profile.pages.artlist');
  const { stateHandler, cacheHandler } = useStateHandler();
  const [loading, setLoading] = useState(false);
  const artistName = getArtworkArtistDisplayName(artwork);

  async function addArtworkToArtlist() {
    // ── if we're in the "board-artwork" artlist, cap at 5 ──
    if (artlistData._id === "board-artwork" && artlistData.artlist_artworks.length >= 5) {
      // you can swap this out for a custom toast if you like
      alert(t('messages.max_carousel_items'));
      return;
    }
    setLoading(true);
    let artlistCoverPayload = null;

    const shouldSeedCover = !artlistData.artlist_image && artlistData.artlist_artworks.length === 0;
    if (shouldSeedCover) {
      try {
        const artworkMainPicture = artwork.artwork_media?.artwork_main_picture;
        if (artworkMainPicture) {
          const croppedBlob = await autoCropImage(artworkMainPicture);
          const { data: imageData } = await UserService.uploadImage(croppedBlob);
          if (imageData?.image_id) {
            artlistCoverPayload = {
              image_id: imageData.image_id,
              image_original_data: imageData.image_original_data,
              source_artwork_id: artwork._id,
            };
          } else {
            console.warn("ArtlistRow: Upload response did not return a valid image_id.");
          }
        }
      } catch (error) {
        console.error("ArtlistRow: Cover generation error for new artlist.", error);
      }
    }

    try {
      const { response, data } = await UserService.addArtworkToArtlist(artwork, artlistData._id, artlistCoverPayload);

      if (response.ok) {
        const newArtworks = [...artlistData.artlist_artworks, artwork];
        const artlist = {
          ...artlistData,
          artlist_image: data.artlist_image || artlistCoverPayload,
          artlist_artworks: newArtworks,
        };

        setArtlistData(artlist);
        // only show the “added to Artlist” popup for real artlists, not the board-artwork flow
        if (artlistData._id !== "board-artwork") {
          const artworkTitle = artwork.artwork_about.artwork_title;
          stateHandler.set("temporalPopup", {
            text: t('temporal.artwork_added', { artworkTitle }),
          });
        }
        cacheHandler.triggerAction("ADD_TO_ARTLIST");
      } else if (data.error_type === "MAX_ARTLIST_ARTWORKS") {
        alert(t('messages.max_artlist_items'));
      } else {
        console.error("ArtlistRow: Unexpected add-artwork response", data);
      }
    } catch (error) {
      console.error("ArtlistRow: Failed to add artwork to artlist", error);
    } finally {
      setLoading(false);
    }
  }

  async function removeArtworkFromArtlist() {
    setLoading(true);

    const { response, data } = await UserService.removeArtworkFromArtlist(artwork, artlistData._id);

    if (response.ok) {
      setLoading(false);
      setArtlistData(prevValue => ({
        ...data,
        artlist_artworks: prevValue.artlist_artworks.filter(artlistArtwork => artlistArtwork._id !== artwork._id)
      }));
      // only show the “removed from Artlist” popup for real artlists, not the board-artwork flow
      if (artlistData._id !== "board-artwork") {
        const artworkTitle = artwork.artwork_about.artwork_title;
        stateHandler.set("temporalPopup", {
          text: t('temporal.artwork_removed', { artworkTitle }),
        });
      }
      cacheHandler.triggerAction("REMOVE_FROM_ARTIST");
    }
  }

  return (
    <div className={`artlist-add-artworks__artwork-result element-non-selectable ${isInArtlist ? "active" : ""}`}>
      <div className="artlist-add-artworks__artwork-info">
        <ArtworkImage 
          image={artwork.artwork_media.artwork_main_picture.image_id} 
          imageClassName="artlist-add-artwork__artwork-image" 
          imageTemplateClassName="artlist-add-artwork__artwork-image"
        />

        <div className="artlist-add-artworks__artwork-text-container">
          <Text
            className="artlist-add-artworks__artwork-title"
            paragraph>
            {artwork.artwork_about.artwork_title}
          </Text>

          <Text className="artlist-add-artworks__artwork-artist" paragraph>
            {t('labels.by')} {artistName}
          </Text>
        </div>
      </div>
      {loading ? (
        <div className="artlist-add-artworks__spinner-wrapper">
          <div className="loader-position-wrapper">
            <AddArtworkToArtlistLoader />
          </div>
        </div>
      ) : isInArtlist ? (
        <PublicSuccessCheck
          className="artlist-add-artworks__artwork-added"
          onClick={removeArtworkFromArtlist}
        /> 
      ) : (
        <AddIcon className="artlist-add-artworks__artwork-add" onClick={addArtworkToArtlist}/>
      )}
    </div>
  );
}
